/* Recomendaciones "Combina tu look" del carrito lateral.
   Rendimiento: no hace nada hasta que el carrito se abre, y los datos de productos
   llegan en UNA sola petición (sections/cart-recs-data.liquid) que se guarda
   10 minutos en la sesión. El contenido del carrito viene ya en los data-atributos.
   Prioridad: misma familia (p. ej. "Ciara Vintage"), mismo color y, si el carrito
   tiene un bolso, accesorios; si no, bolsos. */
(() => {
  if (customElements.get('cart-recs')) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CACHE_KEY = 'cartRecsData:v2';
  const CACHE_MS = 10 * 60 * 1000;
  const COLORS = ['coffee', 'black', 'brown', 'burgundy', 'white', 'cream', 'blue', 'canvas', 'suede'];
  let dataPromise = null;

  const loadData = () => {
    if (dataPromise) return dataPromise;
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.t < CACHE_MS) {
        dataPromise = Promise.resolve(cached.d);
        return dataPromise;
      }
    } catch (e) {
      /* sin sessionStorage: seguimos sin caché */
    }
    dataPromise = fetch(`${root}?section_id=cart-recs-data`)
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-recs-json]');
        const data = node ? JSON.parse(node.textContent) : [];
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), d: data }));
        } catch (e) {
          /* ignorar */
        }
        return data;
      })
      .catch(() => {
        dataPromise = null;
        return [];
      });
    return dataPromise;
  };

  const escapeHtml = (str) =>
    String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const formatMoney = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    if (currency === 'USD') return '$' + (cents / 100).toFixed(2);
    try {
      return (cents / 100).toLocaleString('es-ES', { style: 'currency', currency });
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + currency;
    }
  };

  class CartRecs extends HTMLElement {
    connectedCallback() {
      const drawer = this.closest('cart-drawer');
      if (!drawer || drawer.classList.contains('active')) {
        this.render();
        return;
      }
      this.observer = new MutationObserver(() => {
        if (drawer.classList.contains('active')) {
          this.observer.disconnect();
          this.render();
        }
      });
      this.observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    async render() {
      if (this.rendered) return;
      this.rendered = true;

      const products = await loadData();
      if (!this.isConnected) return;

      const inCart = new Set((this.dataset.inCart || '').split(',').filter(Boolean));
      const family = this.dataset.family || '';
      const firstKey = this.dataset.key || '';
      const color = COLORS.find((c) => firstKey.includes(c));
      const cartHasBag = this.dataset.hasBag === 'true';
      const limit = parseInt(this.dataset.limit || '4', 10);

      const picks = products
        .filter((p) => !inCart.has(p.handle))
        .map((p, index) => {
          const title = (p.title || '').toLowerCase();
          const key = `${p.sku || ''} ${p.handle}`.toLowerCase();
          const isBag = (p.type || '').toLowerCase() === 'bags';
          let score = 0;
          if (family && (title.startsWith(family) || family.startsWith(title))) score += 3;
          if (color && key.includes(color)) score += 2;
          if (cartHasBag !== isBag) score += 2;
          return { p, score, index };
        })
        .sort((a, b) => b.score - a.score || a.index - b.index)
        .slice(0, limit);

      const list = this.querySelector('[data-recs-list]');
      if (!picks.length || !list) {
        this.hidden = true;
        return;
      }

      list.innerHTML = picks
        .map(({ p }) => {
          const compare =
            p.compare_at_price && p.compare_at_price > p.price
              ? `<s class="cart-recs__compare">${formatMoney(p.compare_at_price)}</s>`
              : '';
          return `
            <li class="cart-recs__card">
              <a class="cart-recs__media" href="${p.url}">
                <img src="${p.image}" alt="${escapeHtml(p.title)}" width="120" height="120" loading="lazy" decoding="async">
              </a>
              <div class="cart-recs__info">
                <a class="cart-recs__title" href="${p.url}">${escapeHtml(p.title)}</a>
                <p class="cart-recs__price">
                  <span class="cart-recs__sale">${formatMoney(p.price)}</span>
                  ${compare}
                </p>
                <button type="button" class="cart-recs__add" data-variant-id="${p.variant_id}">+ Añadir al carrito</button>
              </div>
            </li>`;
        })
        .join('');

      list.querySelectorAll('.cart-recs__add').forEach((button) => {
        button.addEventListener('click', () => this.addToCart(button));
      });

      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-ready'));
    }

    async addToCart(button) {
      button.disabled = true;
      button.textContent = 'Añadiendo…';
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        });
        const state = await response.json();
        if (state.status) throw new Error(state.description || 'Error');
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function' && state.sections) {
          drawer.renderContents(state);
        } else {
          window.location.href = `${root}cart`;
        }
      } catch (e) {
        button.disabled = false;
        button.textContent = 'No se pudo añadir';
      }
    }
  }

  customElements.define('cart-recs', CartRecs);
})();
